<template>
  <div
    class="credit-pack-order-summary flex flex-col gap-24 p-24 rounded-12 bg-neutral-background-weak"
  >
    <div>
      <h3 class="text-neutral-text-strong text-m font-bold mb-16">
        Credit purchase summary
      </h3>
      <dl class="flex flex-col gap-8">
        <div class="flex items-center justify-between">
          <dt class="text-neutral-text">Credit pack</dt>
          <dd
            class="text-neutral-text-strong text-right"
            data-testid="credit-pack-summary-credits"
          >
            {{ pack ? formatNumberWithCommas(pack.credits) : '--' }}
          </dd>
        </div>
      </dl>
    </div>
    <BittsDivider class="my-0" />
    <div class="flex items-end justify-between">
      <span class="text-neutral-text-strong text-m font-bold">
        Additional amount
      </span>
      <span
        class="text-success-text text-lg font-bold"
        data-testid="credit-pack-summary-total"
      >
        {{ pack ? currencyFormatter(pack.unit_amount_cents / 100) : '--' }}
      </span>
    </div>
    <p class="text-neutral-text text-xs">
      Purchase authorization - by confirming this purchase, I represent that I
      am authorized to make financial commitments on behalf of
      {{ organizationName }} and that this purchase is subject to the billing
      terms of my organization's existing Crossbeam subscription agreement.
    </p>
    <BittsCheckbox
      :checked="isAuthorized"
      :disabled="isDisabled"
      label="I confirm the above and authorize this purchase on behalf of my organization"
      data-testid="credit-pack-authorization-checkbox"
      @input="emit('update:isAuthorized', $event)"
    />
    <BittsCallout
      v-if="failure"
      type="danger"
      size="x-small"
      role="alert"
      :title="failureMessage"
      data-testid="credit-pack-purchase-failure"
    />
    <div class="flex flex-col gap-8 w-full">
      <BittsButton
        text="Confirm purchase"
        type="primary"
        size="large"
        class="w-full"
        :disabled="!isAuthorized || isDisabled"
        :loading="isSubmitting"
        data-testid="credit-pack-confirm-purchase"
        @click="emit('confirm')"
      />
      <BittsButton
        text="Cancel"
        type="neutral"
        variant="ghost"
        size="large"
        class="w-full"
        :disabled="isSubmitting"
        data-testid="credit-pack-cancel-purchase"
        @click="emit('cancel')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsCheckbox,
  BittsDivider,
} from '@crossbeam/bitts';

import type { CreditPackError } from '@/composables/credits/useCreditPacks';
import type { CreditPack } from '@/types/billing';
import { currencyFormatter, formatNumberWithCommas } from '@/utils';

defineProps<{
  pack: CreditPack | null;
  organizationName: string;
  isAuthorized: boolean;
  isSubmitting: boolean;
  isDisabled: boolean;
  failure: CreditPackError | null;
}>();

const emit = defineEmits<{
  'update:isAuthorized': [value: boolean];
  confirm: [];
  cancel: [];
}>();

/* AIP-709 replaces this with copy per failure.type and statusCode. */
const failureMessage = "We couldn't complete your purchase. Please try again.";
</script>
